import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { userIdentity } from "../lib/identity";
import { useCountryConfig } from "../lib/useCountryConfig";
import { ArrowLeft, MessageSquare, Paperclip, AlertOctagon, Loader2, RefreshCcw, FileText, Image as ImageIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_META = {
  normal:    { label: "Normal",    cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  important: { label: "Important", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  urgent:    { label: "Urgent",    cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return iso; }
};

const AttachmentLink = ({ att, phone }) => {
  if (!att) return null;
  const Icon = (att.content_type || "").startsWith("image/") ? ImageIcon : FileText;
  // The backend already exposes a relative `download_url`; we prefix
  // REACT_APP_BACKEND_URL + add the phone query so the auth check passes.
  const href = `${BACKEND}${att.download_url}?reporter_phone=${encodeURIComponent(phone)}`;
  const sizeMb = att.size ? `${(att.size / 1048576).toFixed(1)} Mo` : "";
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid="msg-attachment-link"
      className="mt-2 inline-flex items-center gap-2 px-3 py-2 border-2 border-rescue-line bg-rescue-bg hover:border-mali-navy hover:bg-mali-navy/5 transition-colors text-rescue-ink"
    >
      <Icon className="w-4 h-4 text-mali-navy" />
      <span className="font-mono text-[12px] truncate max-w-[200px]">{att.filename || "pièce jointe"}</span>
      {sizeMb && <span className="text-[10px] text-rescue-mute">· {sizeMb}</span>}
    </a>
  );
};

export default function MessagesPage() {
  const navigate = useNavigate();
  const cfg = useCountryConfig();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState(() => userIdentity.getPhone());

  const load = useCallback(async () => {
    const p = userIdentity.getPhone();
    setPhone(p);
    if (!p) {
      setError("Identifiez-vous avec votre numéro depuis l'accueil.");
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/messages", { params: { reporter_phone: p, limit: 100 } });
      setItems(data.items || []);
      setError(null);
    } catch (_e) {
      setError("Impossible de charger vos messages.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => { if (mounted) await load(); };
    run();
    const id = setInterval(run, 45_000);
    return () => { mounted = false; clearInterval(id); };
  }, [load]);

  const markRead = async (id) => {
    const p = userIdentity.getPhone();
    if (!p) return;
    try {
      await api.post(`/messages/${id}/read`, null, { params: { reporter_phone: p } });
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch (_e) { /* silent */ }
  };

  return (
    <div className="min-h-[100dvh] bg-rescue-bg flex flex-col" data-testid="messages-page">
      <header className="px-5 pt-5 pb-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="messages-back"
          className="p-2 -ml-2 text-rescue-ink hover:text-mali-red"
          aria-label="Retour"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute" data-testid="messages-brand">{cfg.brand_name} · Messages</div>
          <h1 className="font-display text-2xl font-black tracking-tight leading-none mt-0.5">
            <MessageSquare className="w-5 h-5 inline mr-1.5 align-text-bottom text-mali-navy" />
            Messages reçus
          </h1>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="messages-refresh"
          className="ml-auto p-2 text-rescue-mute hover:text-rescue-ink"
          aria-label="Rafraîchir"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <div className="px-5 pb-8 space-y-3">
        {loading && items.length === 0 && (
          <div className="py-12 text-center text-sm text-rescue-mute flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin" /> Chargement…
          </div>
        )}
        {error && (
          <div className="border-2 border-rose-300 bg-rose-50 text-rose-800 px-3 py-3 text-sm" data-testid="messages-error">
            {error}
          </div>
        )}
        {!loading && items.length === 0 && !error && (
          <div className="py-12 text-center text-sm text-rescue-mute" data-testid="messages-empty">
            Aucun message pour le moment.
          </div>
        )}
        {items.map((m) => {
          const pm = PRIORITY_META[m.priority] || PRIORITY_META.normal;
          const isUrgent = m.priority === "urgent";
          return (
            <article
              key={m.id}
              data-testid={`message-row-${m.id}`}
              onClick={() => !m.read && markRead(m.id)}
              className={`relative border-2 bg-white p-4 cursor-default ${
                isUrgent ? "border-rose-500" : !m.read ? "border-rescue-ink" : "border-rescue-line"
              }`}
            >
              {isUrgent && <span className="absolute left-0 top-0 bottom-0 w-1 bg-rose-500" aria-hidden />}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span className={`shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] border ${pm.cls}`}>
                    {isUrgent && <AlertOctagon className="w-3 h-3 inline mr-0.5" />}{pm.label}
                  </span>
                  {!m.read && <span className="shrink-0 w-2 h-2 rounded-full bg-mali-red" />}
                  {m.sender_admin_name && (
                    <span className="text-[10px] font-mono text-rescue-mute">par {m.sender_admin_name}</span>
                  )}
                </div>
                <span className="font-mono text-[10px] text-rescue-mute shrink-0">{fmt(m.created_at)}</span>
              </div>
              <h2 className="mt-2 font-display text-lg font-black tracking-tight text-rescue-ink leading-tight">{m.title}</h2>
              <p className="mt-1 text-sm text-rescue-ink whitespace-pre-wrap leading-relaxed">{m.body}</p>
              {m.attachment && <AttachmentLink att={m.attachment} phone={phone} />}
            </article>
          );
        })}
      </div>
    </div>
  );
}
